import { useId, useState, type ChangeEvent } from "react";
import { useApp } from "@/app/AppContext";
import { ImportError } from "@/app/useAppData";
import { StatusMessage, type Message } from "@/components/StatusMessage";
import { isSuperadmin } from "@/domain/permissions";
import { useI18n } from "@/i18n/useI18n";
import { format } from "@/i18n/translator";

/**
 * Opens a local workbook for viewing. In individual mode a superadmin can then
 * import it into the database (interventions upserted by ID, Annex 4 sheets
 * replacing the system analysis).
 */
export function LocalFileLoader({ compact = false }: { compact?: boolean }) {
  const { t } = useI18n();
  const { authMode, user, data } = useApp();
  const inputId = useId();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const canImport = authMode === "individual" && isSuperadmin(user);

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setMessage(null);
    try {
      await data.openLocalWorkbook(file);
    } catch (error) {
      setMessage({ kind: "error", text: `${t("localFileError")} ${(error as Error).message}` });
    }
  };

  const importWorkbook = async () => {
    const rows = data.localWorkbook?.workbook.interventionRows ?? [];
    if (!rows.some((row) => String(row.ID ?? "").trim())) {
      setMessage({ kind: "error", text: t("syncToDbNoRows") });
      return;
    }
    setBusy(true);
    try {
      const result = await data.importLocalWorkbook();
      const systemText = result.system
        ? format(t("syncToDbSystemDone"), result.system)
        : t("syncToDbNoSystem");
      setMessage({
        kind: "success",
        text: `${format(t("syncToDbDone"), { count: result.interventions })} ${systemText}`,
      });
    } catch (error) {
      const prefix =
        error instanceof ImportError && error.interventionsImported
          ? t("syncToDbPartial")
          : t("syncToDbFailed");
      setMessage({ kind: "error", text: `${prefix} ${(error as Error).message}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id={compact ? "file-loader-wrapper-system" : "file-loader-wrapper"}>
      <span id={compact ? "label-file-system" : "label-file"}>{t("labelFile")}</span>{" "}
      <label htmlFor={inputId} className="file-label">
        {data.localWorkbook?.fileName ?? t("fileButton")}
      </label>
      <input type="file" id={inputId} accept=".xlsx,.xls" onChange={onFile} hidden />
      {canImport && !compact && (
        <button
          type="button"
          className="file-label"
          disabled={busy || !data.localWorkbook}
          onClick={importWorkbook}
        >
          {busy ? t("syncToDbButtonWorking") : t("syncToDbButton")}
        </button>
      )}
      <StatusMessage message={message} />
    </div>
  );
}
